import { useQueryClient, useMutation } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { invalidateContentArtifacts } from './useArtifacts';
import useTakerMarkets, { TakerMarket } from './useTakerMarkets';

type Referrer = paths['/offers/instant-pay']['post']['requestBody']['content']['application/json']['referrer'];

export interface SubmitInstantPayOfferParams {
  rate: number;
  rateType: 'APR' | 'DISC';
  makerOrganizationUuid: string;
  marketUuid: string;
  supplierDivisionUuid: string;
  referrer?: Referrer;
}

const useSubmitInstantPayOffer = () => {
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const queryClient = useQueryClient();
  const { data: takerMarkets } = useTakerMarkets();
  const { refetchOneTakerMarket } = useRefetchStatsQueries();

  return useMutation({
    mutationKey: ['useSubmitInstantPayOffer'],
    mutationFn: async (variables: SubmitInstantPayOfferParams) => {
      const { rate, rateType, makerOrganizationUuid, marketUuid, supplierDivisionUuid, referrer } = variables;

      const response = await apiClient.post('api/supplier-experience/offers/instant-pay', {
        json: {
          groupingKey: 'SUPPLIER_MARKET',
          frequency: 'one-time',
          rateType,
          rate,
          supplierMarkets: [
            {
              makerOrganizationUuid,
              marketUuid,
              supplierDivisionUuid,
            },
          ],
          referrer,
        } satisfies paths['/offers/instant-pay']['post']['requestBody']['content']['application/json'],
      });

      const body =
        await response.json<paths['/offers/instant-pay']['post']['responses']['201']['content']['application/json']>();

      return { status: response.status, offerUuid: body.data.offerId };
    },
    onSuccess: (result, variables) => {
      const tmMeta = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      if (tmMeta?.marketUuid && tmMeta?.takerDivisionId) {
        queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) =>
          (prevTakerMarkets ?? []).map((tm) => ({
            ...(tm.marketUuid === variables.marketUuid && tm.takerDivisionUuid === variables.supplierDivisionUuid
              ? {
                  ...tm,
                  offerConfig: {
                    ...tm.offerConfig,
                    isDiscountBidding: variables.rateType === 'DISC',
                    maxDiscount: variables.rateType === 'DISC' ? variables.rate : tm.offerConfig.maxDiscount,
                    maxApr: variables.rateType === 'APR' ? variables.rate : tm.offerConfig.maxApr,
                    isEnabled: true,
                    uuid: result.offerUuid,
                    offerType: 'instant-pay',
                    isManuallyAwarded: true,
                  },
                  disableAfterMarketClearsDate: null,
                  offerSchedule: undefined,
                }
              : tm),
          }))
        );

        // instant pay offers always resolve synchronously, refetch immediately
        trackEnterpriseEvent('polling-update::in-time', {
          marketUuid: tmMeta.marketUuid,
          takerDivisionUuid: tmMeta.takerDivisionUuid,
          makerOrganizationUuid: tmMeta.makerOrganizationUuid,
        });
        refetchOneTakerMarket({ marketUuid: tmMeta.marketUuid, takerId: tmMeta.takerDivisionId });
      }

      // Marketing artifacts can display messages promoting offers.
      // Invalidate so we stop marketing offers the supplier just submitted.
      invalidateContentArtifacts(queryClient);
    },
    onError: (error, variables) => {
      const takerMarket = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      addError('useSubmitInstantPayOffer', {
        error,
        variables,
        seaOfferUuid: takerMarket?.offerConfig.uuid,
      });
    },
  });
};

export default useSubmitInstantPayOffer;
